/**
 * Budget Overview Component
 *
 * Displays current budget status and usage history for researchers.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  CircularProgress,
  TablePagination,
  Grid
} from '@mui/material';
import { Warning as WarningIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import * as researcherApi from '../../services/researcherApi';

const BudgetOverview = () => {
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 0, limit: 10, total: 0 });

  useEffect(() => {
    loadBudgetData();
  }, []);

  useEffect(() => {
    loadHistory();
  }, [pagination.page, pagination.limit]);

  const loadBudgetData = async () => {
    try {
      setLoading(true);
      const status = await researcherApi.getBudgetStatus();
      setBudgetStatus(status);
      setError(null);
    } catch (err) {
      setError(err.error || 'Failed to load budget status');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const result = await researcherApi.getBudgetHistory({
        page: pagination.page + 1,
        limit: pagination.limit
      });
      setHistory(result.logs || []);
      setPagination(prev => ({
        ...prev,
        total: result.pagination?.total || 0
      }));
    } catch (err) {
      console.error('Failed to load budget history:', err);
    }
  };

  const formatCurrency = (value) => `$${(value || 0).toFixed(2)}`;

  const getUsagePercentage = (usage, limit) => {
    if (!limit || limit === 0) return 0;
    return Math.min((usage / limit) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'primary';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const dailyPercentage = getUsagePercentage(budgetStatus?.dailyUsage, budgetStatus?.dailyLimit);
  const monthlyPercentage = getUsagePercentage(budgetStatus?.monthlyUsage, budgetStatus?.monthlyLimit);

  return (
    <Box>
      {/* Budget Exhausted Warning */}
      {budgetStatus?.budgetExhausted && (
        <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
          Your budget is exhausted. Contact an administrator to refresh your budget.
        </Alert>
      )}

      {/* Budget Status Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Daily Budget */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Daily Budget</Typography>
              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    Used: {formatCurrency(budgetStatus?.dailyUsage)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Limit: {formatCurrency(budgetStatus?.dailyLimit)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={dailyPercentage}
                  color={getProgressColor(dailyPercentage)}
                  sx={{ height: 10, borderRadius: 5 }}
                />
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    Remaining: {formatCurrency(budgetStatus?.dailyRemaining)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {dailyPercentage.toFixed(0)}%
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Resets at midnight (last reset: {budgetStatus?.lastDayReset ? new Date(budgetStatus.lastDayReset).toLocaleDateString() : 'N/A'})
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Budget */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Monthly Budget</Typography>
              <Box sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    Used: {formatCurrency(budgetStatus?.monthlyUsage)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Limit: {formatCurrency(budgetStatus?.monthlyLimit)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={monthlyPercentage}
                  color={getProgressColor(monthlyPercentage)}
                  sx={{ height: 10, borderRadius: 5 }}
                />
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    Remaining: {formatCurrency(budgetStatus?.monthlyRemaining)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {monthlyPercentage.toFixed(0)}%
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Resets on 1st of month (last reset: {budgetStatus?.lastMonthReset ? new Date(budgetStatus.lastMonthReset).toLocaleDateString() : 'N/A'})
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status Indicator */}
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        {budgetStatus?.budgetExhausted ? (
          <Chip icon={<WarningIcon />} label="Budget Exhausted" color="error" />
        ) : (
          <Chip icon={<CheckIcon />} label="Budget Available" color="success" />
        )}
      </Box>

      {/* Usage History */}
      <Typography variant="h6" gutterBottom>Usage History</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Model</TableCell>
              <TableCell align="right">Tokens</TableCell>
              <TableCell align="right">Cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No usage history yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              history.map((log, index) => (
                <TableRow key={log._id || index}>
                  <TableCell>
                    {new Date(log.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.sourceType?.replace('_', ' ') || 'Unknown'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{log.model || 'N/A'}</TableCell>
                  <TableCell align="right">
                    {log.tokenUsage?.totalTokens?.toLocaleString() || 'N/A'}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(log.cost)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={(e) => setPagination(prev => ({
            ...prev,
            limit: parseInt(e.target.value, 10),
            page: 0
          }))}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>
    </Box>
  );
};

export default BudgetOverview;
